import dayjs from 'dayjs';

/** Minutes after scheduled start before no-show actions (mark, invoice); matches backend. */
export const NO_SHOW_GRACE_MINUTES_AFTER_START = 15;
/** Minutes after no-show marking before revert needs a special right; matches backend. */
export const NO_SHOW_REVERT_GRACE_MINUTES = 5;
/**
 * Minutes after checkout (finishedAt) during which the spontaneous-reduction
 * ("Spontan weniger Personen") button stays available; matches backend
 * POST_CHECKOUT_GRACE_MINUTES. 2 days.
 */
export const SPONTANEOUS_REDUCTION_POST_CHECKOUT_GRACE_MINUTES = 2 * 24 * 60;

export const isInactiveBookingStatus = status =>
  ['canceled', 'rejected', 'blocked', 'noShow'].includes(status);

export const isTodayOrFuture = input => {
  if (!input) return false;
  const date = dayjs(input);
  if (!date.isValid()) return false;
  return date.isSame(dayjs(), 'day') || date.isAfter(dayjs(), 'day');
};

/**
 * Matches backend buildStaleWaitingForPaymentFilter timing:
 * a payment-related booking state is "actionable" when:
 * - creation >= 2h ago OR
 * - (startTime in < 24h AND creation > 15min ago)
 */
export const isPaymentActionableByTiming = (booking, nowTs = Date.now()) => {
  if (!booking) return false;
  const createdAtRaw =
    booking?.meta?.createdAt || booking?.eventCreatedAt || booking?.createdAt;
  const createdAtTs = createdAtRaw ? new Date(createdAtRaw).getTime() : NaN;
  const startTimeTs = booking?.startTime ? new Date(booking.startTime).getTime() : NaN;
  if (!Number.isFinite(createdAtTs)) return false;

  const twoHoursAgo = nowTs - 2 * 60 * 60 * 1000;
  const fifteenMinutesAgo = nowTs - 15 * 60 * 1000;
  const twentyFourHoursFromNow = nowTs + 24 * 60 * 60 * 1000;

  if (createdAtTs <= twoHoursAgo) return true;
  if (
    Number.isFinite(startTimeTs) &&
    startTimeTs <= twentyFourHoursFromNow &&
    createdAtTs <= fifteenMinutesAgo
  ) {
    return true;
  }
  return false;
};


/**
 * Matches backend markNoShow timing: operator may mark no-show when now is at or after
 * scheduled start plus NO_SHOW_GRACE_MINUTES_AFTER_START.
 */
export const isNoShowActionable = (booking, nowValue = Date.now()) => {
  if (!booking) return false;
  if (booking.status !== 'confirmed') return false;
  if (booking.presentSince) return false;
  if (Number(booking.participantCount || 0) <= 0) return false;

  const start = booking?.startTime ? dayjs(booking.startTime) : null;
  if (!start?.isValid()) return false;

  const now = dayjs(nowValue);
  const eligibleAt = start.add(NO_SHOW_GRACE_MINUTES_AFTER_START, 'minute');
  return now.isAfter(eligibleAt) || now.isSame(eligibleAt);
};

/**
 * Matches backend sendNoShowInvoice timing: invoice send is allowed when
 * now is at or after scheduled start plus NO_SHOW_GRACE_MINUTES_AFTER_START.
 */
export const isNoShowInvoiceSendable = (booking, nowValue = Date.now()) => {
  const start = booking?.startTime ? dayjs(booking.startTime) : null;
  if (!start?.isValid()) return false;
  const now = dayjs(nowValue);
  const eligibleAt = start.add(NO_SHOW_GRACE_MINUTES_AFTER_START, 'minute');
  return now.isAfter(eligibleAt) || now.isSame(eligibleAt);
};

export const hasNoShowInvoiceBillableLineItems = booking => {
  if (!booking || booking.fullyPaid === true) return false;
  const lineItems = Array.isArray(booking.lineItems) ? booking.lineItems : [];
  return lineItems.some(item => {
    if (!item || item.status === 'canceled' || !(item.count > 0)) return false;
    const refs = Array.isArray(item.paymentReferences) ? item.paymentReferences : [];
    return refs.some(
      ref => ref && ref.paymentType === 'offline' && ref.count > 0 && !ref.isPaid
    );
  });
};

export const isNoShowRevertInGracePeriod = (booking, nowValue = Date.now()) => {
  if (!booking || booking.status !== 'noShow') return false;
  if (booking.noShowInvoiceId || booking.noShowInvoiceSkippedAt || booking.noShowInvoicePaidAt) {
    return false;
  }

  const noShowAt = booking?.noShowAt ? dayjs(booking.noShowAt) : null;
  if (!noShowAt?.isValid()) return false;

  const now = dayjs(nowValue);
  const privilegedAt = noShowAt.add(NO_SHOW_REVERT_GRACE_MINUTES, 'minute');
  return now.isBefore(privilegedAt) || now.isSame(privilegedAt);
};

/**
 * Matches backend buildStaleWaitingForPaymentFilter: a waitingForPayment booking
 * is "actionable" (shown in PendingBookings, highlighted in views) when:
 * - creation >= 2h ago OR
 * - (startTime in < 24h AND creation > 15min ago)
 */
export const isWaitingForPaymentActionable = (booking, nowTs = Date.now()) => {
  if (!booking || booking.status !== 'waitingForPayment') return false;
  return isPaymentActionableByTiming(booking, nowTs);
};

/**
 * Free-cancellation/-reduction window in hours before the event start; matches the backend
 * PUBLIC_ACTION_WINDOW (7 days).
 */
export const FREE_REDUCTION_WINDOW_HOURS = 168;

/** True when booking, package or an upsell carries the ticket-insurance tag. */
export const hasTicketInsurance = booking => {
  const tags = booking?.tags || [];
  const pkgTags = booking?.selectedPackage?.tags || [];
  const upsellTags = (booking?.upsells || []).flatMap(u => u?.tags || []);
  return [...tags, ...pkgTags, ...upsellTags].includes('hasInsurance');
};

/**
 * True once the free-reduction deadline has passed — the end of the calendar day that lies
 * FREE_REDUCTION_WINDOW_HOURS before the event start ("bis 7 Tage vorher, bis Tagesende").
 * Mirrors the backend getCancellationDeadline/isWithinCancellationWindow, which anchors the
 * boundary to Europe/Berlin; the dashboards run in that timezone, so the local end of day is
 * the same instant. An unparseable start time yields false (deadline not reached).
 */
export const isPastFreeReductionDeadline = (startTime, nowValue = Date.now()) => {
  const start = startTime ? dayjs(startTime) : null;
  if (!start?.isValid()) return false;
  const deadline = start.subtract(Math.round(FREE_REDUCTION_WINDOW_HOURS / 24), 'day').endOf('day');
  return dayjs(nowValue).isAfter(deadline);
};

/**
 * Whether the staff reduction window is open for this booking: from the free-reduction deadline
 * on, through the visit (3 h grace after the start, while checked in, and after checkout for the
 * post-checkout grace — one month with the late-reduction right). Mirrors the backend
 * isWithinStaffReductionWindow *without* openBeforeStart, i.e. the variant the free-reduction
 * redirect gate uses.
 */
export const isWithinStaffReductionWindow = (booking, opts = {}, nowValue = Date.now()) => {
  if (!booking) return false;
  const start = booking.startTime ? dayjs(booking.startTime) : null;
  if (!start?.isValid()) return false;
  const now = dayjs(nowValue);
  if (!isPastFreeReductionDeadline(booking.startTime, nowValue)) return false;
  if (start.diff(now, 'hour') >= -3) return true;
  if (booking.presentSince && !booking.finishedAt) return true;
  if (booking.finishedAt) {
    if (opts.latePostCheckout) return !now.isAfter(dayjs(booking.finishedAt).add(1, 'month'));
    return (
      now.diff(dayjs(booking.finishedAt), 'minute', true) <=
      SPONTANEOUS_REDUCTION_POST_CHECKOUT_GRACE_MINUTES
    );
  }
  return false;
};

/**
 * True when a plain (refunding) participant reduction would be rejected and "Spontan weniger
 * Personen" (MOP-show voucher) is the only remaining path: the staff reduction window is open,
 * the booking carries no ticket insurance, and the user holds neither override right. Mirrors the
 * backend SPONTANEOUS_REDUCTION_REQUIRED gate (internal-booking cancelParticipants), whose
 * canOverride is unlimitedEdits || forceReduceLastMinute.
 */
export const isPlainReductionBlocked = (booking, opts = {}, nowValue = Date.now()) => {
  if (!booking || opts.canForceReduce) return false;
  if (hasTicketInsurance(booking)) return false;
  return isWithinStaffReductionWindow(
    booking,
    { latePostCheckout: opts.latePostCheckout },
    nowValue
  );
};
